import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Pencil } from 'lucide-react'
import { CustomerInfoSection } from './CustomerInfoSection'
import { CustomerStatsSection } from './CustomerStatsSection'
import { RecurringOrdersSection } from './RecurringOrdersSection'
import { AccountsSection } from './AccountsSection'
import CustomerForm from '../CustomerForm'
import { CustomerPriceListDialog } from './CustomerPriceListDialog'
import { fetchCustomer, fetchFarmInformation, getFarmLogoUrl, getCustomerLogoUrl, fetchPartnerLogo } from '@/supabase-client'
import { generateSplicedLogo, downloadSplicedLogo } from '@/utils/spliceLogoGenerator'
import { toast } from 'sonner'

export function CustomerDetailModal({ customer, open, onOpenChange, onSave }) {
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [priceListOpen, setPriceListOpen] = useState(false)
  const [currentCustomer, setCurrentCustomer] = useState(customer)
  const [refreshKey, setRefreshKey] = useState(0)
  const [farmLogoUrl, setFarmLogoUrl] = useState(null)
  const [partnerLogoUrl, setPartnerLogoUrl] = useState(null)

  // Update current customer when prop changes
  useEffect(() => {
    setCurrentCustomer(customer)
  }, [customer])

  // Fetch farm logo URL and partner logo on mount
  useEffect(() => {
    const loadLogos = async () => {
      try {
        const farmInfo = await fetchFarmInformation()
        setFarmLogoUrl(getFarmLogoUrl(farmInfo.logo_file_path))
      } catch {
        // Farm logo not set — splice won't be available
      }

      if (customer?.id) {
        const partnerLogo = await fetchPartnerLogo(customer.id)
        setPartnerLogoUrl(partnerLogo?.logo_url_dark || null)
      }
    }
    if (open) loadLogos()
  }, [open, customer?.id])

  const handleEditClick = () => {
    setEditModalOpen(true)
  }

  const handleSpliceDownload = async () => {
    const customerLogoUrl = partnerLogoUrl || getCustomerLogoUrl(currentCustomer.logoFilePath)
    if (!farmLogoUrl || !customerLogoUrl) return

    try {
      const blob = await generateSplicedLogo(farmLogoUrl, customerLogoUrl)
      downloadSplicedLogo(blob, currentCustomer.companyName)
      toast.success('Splice logo downloaded')
    } catch (error) {
      console.error('Splice logo error:', error)
      if (error.message?.includes('tainted')) {
        toast.error('Please use PNG or JPG for splice generation')
      } else {
        toast.error('Failed to generate splice logo')
      }
    }
  }

  const handleEditSave = async (customerData) => {
    try {
      // Call parent onSave to update customer (optional — modal still refreshes its own view)
      await onSave?.(customerData)

      // Refetch customer data to get latest info
      const updatedCustomer = await fetchCustomer(currentCustomer.id)

      // Transform customer data to match display format
      const companyBranch = updatedCustomer.branches?.find(branch => branch.branch_type === 'company') ||
        updatedCustomer.branches?.find(branch =>
          branch.name && (
            branch.name.toLowerCase().includes('main') ||
            branch.name.toLowerCase().includes('head') ||
            branch.name.toLowerCase().includes('office')
          )
        ) || updatedCustomer.branches?.[0]

      const transformedCustomer = {
        id: updatedCustomer.id,
        companyName: updatedCustomer.name,
        email: updatedCustomer.email,
        paymentTermsDays: updatedCustomer.payment_terms ? parseInt(updatedCustomer.payment_terms) : 30,
        payment_method: updatedCustomer.payment_method || null,
        phone: updatedCustomer.phone || '',
        reference_code: updatedCustomer.reference_code || null,
        addressLine1: companyBranch?.address_line_1 || '',
        addressLine2: companyBranch?.address_line_2 || '',
        addressLine3: companyBranch?.address_line_3 || '',
        city: companyBranch?.city || '',
        postcode: companyBranch?.postcode || '',
        country: companyBranch?.country || 'United Kingdom',
        logoFilePath: updatedCustomer.logo_file_path || null,
        logoFileName: updatedCustomer.logo_file_name || null,
        logoUploadedAt: updatedCustomer.logo_uploaded_at || null,
        branches: updatedCustomer.branches?.filter(branch =>
          branch.branch_type !== 'company'
        ).map(branch => ({
          id: branch.id,
          branchName: branch.name,
          branchAddress: branch.address,
          branchPhone: branch.phone || '',
          branchEmail: branch.email || '',
          branchType: branch.branch_type || 'branch'
        })) || []
      }

      setCurrentCustomer(transformedCustomer)
      setEditModalOpen(false)

      // Trigger refresh of stats and history sections
      setRefreshKey(prev => prev + 1)
    } catch (error) {
      console.error('Error saving customer:', error)
      throw error
    }
  }

  if (!currentCustomer) return null

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-hidden flex flex-col pt-10">
          <DialogHeader>
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <DialogTitle className="text-xl">{currentCustomer.companyName}</DialogTitle>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleEditClick}
                className="flex-shrink-0"
              >
                <Pencil className="h-4 w-4 mr-2" />
                Edit Customer
              </Button>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-6 pr-2" key={refreshKey}>
            {/* Section 1: Contact Information */}
            <CustomerInfoSection
              customer={currentCustomer}
              farmLogoUrl={farmLogoUrl}
              onSpliceDownload={handleSpliceDownload}
              onPriceListDownload={() => setPriceListOpen(true)}
            />

            {/* Section 2: Key Statistics */}
            <CustomerStatsSection customerId={currentCustomer.id} />

            {/* Section 3: Recurring Orders (conditional) */}
            <RecurringOrdersSection customerId={currentCustomer.id} />

            {/* Section 4: Accounts (unified sales, payments, credits) */}
            <AccountsSection
              customerId={currentCustomer.id}
              customerName={currentCustomer.companyName}
              branches={currentCustomer.branches}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Stacked Edit Modal - renders on top of detail modal */}
      {editModalOpen && (
        <CustomerForm
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          onSave={handleEditSave}
          editingCustomer={currentCustomer}
        />
      )}

      <CustomerPriceListDialog
        open={priceListOpen}
        onOpenChange={setPriceListOpen}
        customerId={currentCustomer.id}
        customerName={currentCustomer.companyName}
      />
    </>
  )
}
